import React, { useEffect, useState } from "react";
import { projectGroups, projects } from "../../data/projects";
import { nodeId } from "../../data/connections";
import EvidenceCard from "../../components/investigation/EvidenceCard";
import EvidenceImage from "../../components/investigation/EvidenceImage";
import DeviceMockup from "../../components/investigation/DeviceMockup";
import ProjectTooltip from "../../components/investigation/ProjectTooltip";
import { detailFromProject } from "../../utils/evidenceDetail";

const ROTATIONS = [-1.2, 0.8, -0.5, 1.4, -0.9];
const PINS = ["red", "black", "brass"];

const byId = Object.fromEntries(projects.map((project) => [project.id, project]));

const ProjectCard = ({ project, index }) => {
  const [anchor, setAnchor] = useState(null);
  const detail = detailFromProject(project);
  const isDocument = project.imageKind === "document";
  const node = nodeId.project(project.id);

  const toggle = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchor((open) => (open ? null : rect));
  };

  // The board pans/zooms under the panel, so a wheel or a press anywhere else closes it.
  useEffect(() => {
    if (!anchor) return undefined;
    const close = (event) => {
      if (!event.target.closest?.(`.project-tip, [data-node="${node}"]`)) setAnchor(null);
    };
    const onKey = (event) => event.key === "Escape" && setAnchor(null);
    window.addEventListener("wheel", close, { passive: true });
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("wheel", close);
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [anchor, node]);

  return (
    <>
      <EvidenceCard
        variant={isDocument ? "polaroid" : "device"}
        pin={isDocument ? PINS[index % PINS.length] : false}
        rotate={isDocument ? ROTATIONS[index % ROTATIONS.length] : 0}
        className={`project-evidence project-evidence--${isDocument ? "document" : project.device || "laptop"}`}
        nodeId={node}
        role="button"
        tabIndex={0}
        aria-expanded={Boolean(anchor)}
        aria-label={`Exhibit ${project.evidenceNo}: ${project.title}`}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle(event);
          }
        }}
      >
        {isDocument ? (
          <EvidenceImage
            src={project.image}
            alt=""
            variant="document"
            placeholderLabel={project.placeholderLabel || "PROJECT SCREENSHOT"}
            pin={false}
          />
        ) : (
          <DeviceMockup
            device={project.device}
            image={project.screenImage || project.image}
            video={project.screenVideo}
          />
        )}
      </EvidenceCard>
      {anchor ? (
        <ProjectTooltip project={project} detail={detail} anchor={anchor} onClose={() => setAnchor(null)} />
      ) : null}
    </>
  );
};

export const ProjectsSection = () => (
  <div className="projects-cluster">
    {projectGroups.map((group) => (
      <div key={group.id} className="evidence-group">
        <p className="evidence-group-label">
          Built with {group.label} <span>· {group.ids.length} exhibits</span>
        </p>
        <div className="evidence-group-row projects-row">
          {group.ids.map((id, index) => {
            const project = byId[id];
            if (!project) return null;
            return <ProjectCard key={id} project={project} index={index} />;
          })}
        </div>
      </div>
    ))}
  </div>
);

export default ProjectsSection;
